import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../auth";
import { api } from "../api";
import {
  Select, SelectContent, SelectItem, SelectSeparator, SelectTrigger, SelectValue,
} from "./ui/select";
import { Settings } from "lucide-react";
import MasterManageDialog from "./MasterManageDialog";

const SINGULAR = {
  recruiters: "Recruiter",
  departments: "Department",
  locations: "Location",
  sources: "Source",
  regions: "Region",
  entities: "Entity",
  units: "Unit",
  business_units: "Business Unit",
  categories: "Category",
  budget_types: "Budget Type",
  employment_types: "Employment Type",
  countries: "Country",
  cities: "City",
  brands: "Brand",
};

const MANAGE_VALUE = "__manage__";
const NONE_VALUE = "__none__";

export default function MasterDropdown({
  kind, value, onChange, placeholder, testId, allowEmpty = false,
}) {
  const { user } = useAuth();
  const isAdmin = user?.role === "Admin";
  const [items, setItems] = useState([]);
  const [manageOpen, setManageOpen] = useState(false);

  const load = useCallback(async () => {
    const r = await api.get(`/masters/${kind}`, { params: { only_active: true } });
    setItems(r.data);
  }, [kind]);

  useEffect(() => { load(); }, [load]);

  const valueInList = !value || items.some((i) => i.name === value);

  return (
    <>
      <Select
        value={value || (allowEmpty ? NONE_VALUE : undefined)}
        onValueChange={(v) => {
          if (v === MANAGE_VALUE) { setManageOpen(true); return; }
          if (v === NONE_VALUE) { onChange(""); return; }
          onChange(v);
        }}
      >
        <SelectTrigger data-testid={testId}>
          <SelectValue placeholder={placeholder || "Select…"} />
        </SelectTrigger>
        <SelectContent>
          {allowEmpty && <SelectItem value={NONE_VALUE}>—</SelectItem>}
          {!valueInList && value && (
            <SelectItem value={value} className="text-muted-foreground italic">
              {value} (inactive)
            </SelectItem>
          )}
          {items.map((it) => (
            <SelectItem key={it.id} value={it.name}>{it.name}</SelectItem>
          ))}
          {isAdmin && (
            <>
              <SelectSeparator />
              <SelectItem
                value={MANAGE_VALUE}
                className="text-primary font-medium focus:text-primary"
                data-testid={`${testId}-manage`}
              >
                <span className="inline-flex items-center gap-2">
                  <Settings className="w-3.5 h-3.5" /> Manage {SINGULAR[kind]}
                </span>
              </SelectItem>
            </>
          )}
        </SelectContent>
      </Select>

      {isAdmin && (
        <MasterManageDialog
          open={manageOpen}
          onOpenChange={setManageOpen}
          kind={kind}
          title={`Manage ${SINGULAR[kind]}s`}
          onChanged={load}
        />
      )}
    </>
  );
}
